/** PROPOZYCJA — DO WERYFIKACJI: task workspace presentation policy, in pixels.
 * History needs 320px of content plus its inset/borders; details need room for field controls.
 * Automatic hiding preserves the caller's explicit open/closed preference.
 */
interface TaskSplitviewLayout {
  historyVisible: boolean;
  list: number;
  details: number;
  detailsMin: number;
  history: number;
  historyMin: number;
}

export function taskSplitviewLayout(
  width: number,
  historyRequested: boolean,
  historyExplicitlyOpened = false
): TaskSplitviewLayout {
  // Manual access remains possible below the three-pane threshold without squeezing controls.
  if (historyRequested && historyExplicitlyOpened && width < 1180) {
    if (width < 796) {
      return { historyVisible: true, list: 0, details: 0, detailsMin: 0, history: 100, historyMin: 100 };
    }
    const availableWidth = width - 12;
    const historyMin = (336 / availableWidth) * 100;
    const detailsMin = (448 / availableWidth) * 100;
    return {
      historyVisible: true,
      list: 0,
      details: 100 - historyMin,
      detailsMin,
      history: historyMin,
      historyMin
    };
  }
  const historyVisible = historyRequested && width >= 1180;
  if (!historyVisible) {
    const detailsMin = width > 0 ? (448 / (width - 12)) * 100 : 0;
    const details = Math.max(38.2, detailsMin);
    return { historyVisible, list: 100 - details, details, detailsMin, history: 0, historyMin: 0 };
  }
  const availableWidth = width - 24; // Two 12px gutters.
  const historyMin = (336 / availableWidth) * 100;
  const history = Math.max(19.1, historyMin);
  const detailsMin = (448 / availableWidth) * 100; // 440px panel + two 4px area insets.
  const details = Math.max(30.9, detailsMin);
  return { historyVisible, list: 100 - details - history, details, detailsMin, history, historyMin };
}

export type TaskSplitviewPane = 'list' | 'details' | 'history';
export type TaskSplitviewPaneSizes = Readonly<Record<TaskSplitviewPane, number>>;

/** Visible panes in split-area order; the same set identifies one layout bucket. */
export function taskSplitviewVisiblePanes(layout: TaskSplitviewLayout): TaskSplitviewPane[] {
  const panes: TaskSplitviewPane[] = [];
  if (layout.list > 0) panes.push('list');
  if (layout.details > 0) panes.push('details');
  if (layout.historyVisible) panes.push('history');
  return panes;
}

/**
 * Keeps user-dragged pane percentages while the workspace is resized within one bucket.
 * Panes below the current width's minimum grow back to it, taking the space from the list
 * first; without a preference (or when the minimums cannot be honoured) the policy defaults apply.
 * Returned values stay identical while no clamping is needed, so angular-split keeps the drag.
 */
export function taskSplitviewPaneSizes(
  layout: TaskSplitviewLayout,
  preferred: TaskSplitviewPaneSizes | null,
  listMin: number
): TaskSplitviewPaneSizes {
  const defaults = { list: layout.list, details: layout.details, history: layout.history };
  if (!preferred) return defaults;
  const panes = taskSplitviewVisiblePanes(layout);
  const mins: TaskSplitviewPaneSizes = { list: listMin, details: layout.detailsMin, history: layout.historyMin };
  const sizes = { list: 0, details: 0, history: 0 };
  for (const pane of panes) sizes[pane] = Math.max(preferred[pane], mins[pane]);
  let excess = sizes.list + sizes.details + sizes.history - 100;
  for (const pane of panes) {
    const transferable = Math.min(excess, sizes[pane] - mins[pane]);
    if (transferable > 0) {
      sizes[pane] -= transferable;
      excess -= transferable;
    }
  }
  return excess > 0.01 ? defaults : sizes;
}
